/* eslint-disable react/no-unknown-property */
/* ============================================================
   Planets – textured worlds for Space mode.
   Planet      : one per section (lava / desert / gas / ice / rocky)
                 with bump relief, atmosphere rim, optional ring
                 system and an alien colony on the surface.
   EarthPlanet : procedural Earth (continents, ice caps, ocean
                 specular via roughness map) + drifting cloud layer.
   ============================================================ */
import { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { Billboard, Html, Text } from "@react-three/drei";
import * as THREE from "three";
import { AIRPORTS } from "@/data/sections";
import { useWorldStore } from "@/components/world/store";
import { getPlanetTextures, getEarthTextures, getRingTexture, PLANET_TYPES } from "@/components/world/textures";
import { TinyAlien } from "@/components/world/models/Characters";

const PLANET_R = 22;

/* Fresnel-ish atmosphere: two back-side shells with additive blending */
function Atmosphere({ radius, color, active, auraRef }) {
  return (
    <>
      <mesh ref={auraRef} scale={1.12}>
        <sphereGeometry args={[radius, 32, 24]} />
        <meshBasicMaterial color={color} transparent opacity={0.14} depthWrite={false} side={THREE.BackSide} blending={THREE.AdditiveBlending} />
      </mesh>
      <mesh scale={1.04}>
        <sphereGeometry args={[radius, 32, 24]} />
        <meshBasicMaterial color={color} transparent opacity={active ? 0.16 : 0.09} depthWrite={false} side={THREE.BackSide} blending={THREE.AdditiveBlending} />
      </mesh>
    </>
  );
}

function AlienColony({ color }) {
  return (
    <group position={[0, PLANET_R * 0.985, 0]}>
      {/* habitat domes */}
      {[
        [0, 0, 2.6],
        [2.4, 0.6, 1.6],
        [-2.2, 1.1, 1.4],
        [1.2, -2.3, 1.2],
      ].map(([dx, dz, r], i) => (
        <group key={i} position={[dx, 0, dz]}>
          <mesh>
            <sphereGeometry args={[r, 18, 12, 0, Math.PI * 2, 0, Math.PI / 2]} />
            <meshStandardMaterial color="#DDEFF6" transparent opacity={0.45} metalness={0.2} roughness={0.08} emissive="#8AC5FF" emissiveIntensity={0.25} depthWrite={false} />
          </mesh>
          <mesh>
            <torusGeometry args={[r, 0.06, 6, 32]} />
            <meshStandardMaterial color="#B9BCC0" metalness={0.9} roughness={0.3} />
          </mesh>
          <mesh position={[0, r * 0.35, 0]} scale={[1, 0.35, 1]}>
            <sphereGeometry args={[r * 0.55, 10, 8]} />
            <meshStandardMaterial color="#3B4652" metalness={0.5} roughness={0.5} />
          </mesh>
        </group>
      ))}
      {/* comms tower */}
      <mesh position={[-1.4, 2.2, -2.4]}>
        <cylinderGeometry args={[0.06, 0.16, 4.4, 8]} />
        <meshStandardMaterial color="#C8CCD2" metalness={0.9} roughness={0.3} />
      </mesh>
      <mesh position={[-1.4, 4.5, -2.4]}>
        <sphereGeometry args={[0.22, 10, 8]} />
        <meshStandardMaterial color={color} emissive={color} emissiveIntensity={1.6} toneMapped={false} />
      </mesh>
      {/* landing pad */}
      <mesh position={[3.2, 0.02, -2.6]} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[0.9, 1.5, 24]} />
        <meshStandardMaterial color={color} emissive={color} emissiveIntensity={0.8} side={THREE.DoubleSide} />
      </mesh>
    </group>
  );
}

export function Planet({ airport, index }) {
  const [x, , z] = airport.position;
  const py = (index % 2 === 0 ? -8 : 12) + Math.sin(index * 2) * 6;
  const nearestId = useWorldStore((s) => s.nearestId);
  const active = nearestId === airport.id;
  const rotRef = useRef();
  const ringRef = useRef();
  const auraRef = useRef();
  const type = PLANET_TYPES[index % PLANET_TYPES.length];
  const hasRing = type === "gas" || index === 4;

  const tex = useMemo(() => getPlanetTextures(type, airport.color, index + 1), [type, airport.color, index]);
  const ringTex = useMemo(() => (hasRing ? getRingTexture(airport.color, index + 3) : null), [hasRing, airport.color, index]);

  const material = useMemo(() => {
    const m = new THREE.MeshStandardMaterial({
      map: tex.map || null,
      bumpMap: tex.bumpMap || null,
      bumpScale: type === "gas" ? 0.15 : 0.9,
      roughness: type === "ice" ? 0.45 : type === "gas" ? 0.75 : 0.95,
      metalness: type === "ice" ? 0.15 : 0.02,
      color: tex.map ? "#FFFFFF" : airport.color,
    });
    if (tex.emissiveMap) {
      m.emissiveMap = tex.emissiveMap;
      m.emissive = new THREE.Color("#FF7A2A");
      m.emissiveIntensity = 1.6;
    } else {
      // faint self-illumination so the dark side stays legible in space
      m.emissive = new THREE.Color(airport.color);
      m.emissiveIntensity = 0.06;
    }
    return m;
  }, [tex, type, airport.color]);

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    if (rotRef.current) rotRef.current.rotation.y += type === "gas" ? 0.0022 : 0.0012;
    if (ringRef.current) ringRef.current.rotation.z += 0.0006;
    if (auraRef.current) auraRef.current.material.opacity = 0.12 + (active ? 0.14 : 0) + Math.sin(t * 2) * 0.03;
  });

  const aliens = useMemo(() => {
    const arr = [];
    const positions = [
      { lat: 1.1, lon: 0 },
      { lat: 1.15, lon: 1.2 },
      { lat: 1.15, lon: -1.2 },
      { lat: 1.4, lon: 2.4 },
      { lat: 1.4, lon: -2.4 },
      { lat: 0.85, lon: 0.7 },
    ];
    positions.forEach((pos, i) => {
      const lat = pos.lat;
      const lon = pos.lon + index * 0.4;
      const cx = Math.sin(lat) * Math.cos(lon) * PLANET_R * 0.99;
      const cz = Math.sin(lat) * Math.sin(lon) * PLANET_R * 0.99;
      const cy = Math.cos(lat) * PLANET_R * 0.99;
      const n = new THREE.Vector3(cx, cy, cz).normalize();
      const up = new THREE.Vector3(0, 1, 0);
      const q = new THREE.Quaternion().setFromUnitVectors(up, n);
      const e = new THREE.Euler().setFromQuaternion(q);
      arr.push({ x: cx, y: cy, z: cz, rx: e.x, ry: e.y, rz: e.z, seed: i * 1.7 + index });
    });
    return arr;
  }, [index]);

  return (
    <group position={[x, py, z]}>
      <Atmosphere radius={PLANET_R} color={airport.color} active={active} auraRef={auraRef} />
      <group ref={rotRef} rotation={[0.18, 0, 0.12]}>
        <mesh material={material}>
          <sphereGeometry args={[PLANET_R, 64, 48]} />
        </mesh>
        {aliens.map((a, i) => (
          <TinyAlien key={i} x={a.x} y={a.y} z={a.z} rx={a.rx} ry={a.ry} rz={a.rz} seed={a.seed} />
        ))}
        <AlienColony color={airport.color} />
      </group>

      {hasRing && (
        <mesh ref={ringRef} rotation={[Math.PI / 2.4, 0.25, 0]}>
          <ringGeometry args={[PLANET_R * 1.35, PLANET_R * 2.15, 128, 1]} />
          <meshStandardMaterial
            map={ringTex || null}
            color={ringTex ? "#FFFFFF" : airport.color}
            transparent
            opacity={0.95}
            side={THREE.DoubleSide}
            roughness={0.9}
            metalness={0}
            depthWrite={false}
            alphaTest={0.02}
          />
        </mesh>
      )}

      <Billboard position={[0, PLANET_R + 8, 0]} follow>
        <Text fontSize={4.4} color={airport.color} anchorX="center" anchorY="middle" outlineWidth={0.08} outlineColor="#050308" letterSpacing={0.05}>
          {airport.label}
        </Text>
      </Billboard>
      <Billboard position={[0, PLANET_R + 4, 0]} follow>
        <Text fontSize={0.9} color="#C5A8FF" anchorX="center" anchorY="middle" letterSpacing={0.35}>
          {`> ${airport.code} · SECTOR ${(index + 1).toString().padStart(2, "0")}`}
        </Text>
      </Billboard>

      {active && (
        <Html center position={[0, PLANET_R + 1, 0]} zIndexRange={[10, 0]} distanceFactor={20}>
          <div className="pointer-events-none select-none whitespace-nowrap px-4 py-2 rounded-md bg-[rgba(20,10,40,0.85)] border border-[rgba(140,90,255,0.6)] text-[#F4EADE] font-mono text-xs uppercase tracking-[0.24em] shadow-[0_0_40px_rgba(140,90,255,0.5)]">
            <span className="text-[#C5A8FF]">[E]</span> Dock · {airport.label}
          </div>
        </Html>
      )}
      <pointLight color={airport.color} intensity={active ? 2.2 : 1.2} distance={80} />
    </group>
  );
}

/* ============================================================
   EarthPlanet – the way back home
   ============================================================ */
export function EarthPlanet() {
  const spinRef = useRef();
  const cloudRef = useRef();
  const auraRef = useRef();
  const nearestId = useWorldStore((s) => s.nearestId);
  const active = nearestId === "earth-return";

  const POS = [-30, 20, 240];
  const R = 26;
  const tex = useMemo(() => getEarthTextures(), []);
  const surfaceMat = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        map: tex.map || null,
        roughnessMap: tex.roughnessMap || null,
        roughness: 1,
        metalness: 0.12,
        color: tex.map ? "#FFFFFF" : "#12466A",
        emissive: new THREE.Color("#0B2A44"),
        emissiveIntensity: 0.12,
      }),
    [tex]
  );
  const cloudMat = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        map: tex.clouds || null,
        transparent: true,
        opacity: tex.clouds ? 0.9 : 0.35,
        depthWrite: false,
        roughness: 1,
        metalness: 0,
        color: "#FFFFFF",
      }),
    [tex]
  );

  const landmasses = useMemo(() => {
    const arr = [];
    AIRPORTS.forEach((ap, i) => {
      const norm = 220;
      const px = ap.position[0] / norm;
      const pz = ap.position[2] / norm;
      const lon = Math.atan2(px, pz) + i * 0.1;
      const latOff = (Math.abs(px) + Math.abs(pz)) * 0.5;
      const lat = 1.0 + latOff;
      const cx = Math.sin(lat) * Math.cos(lon) * R * 0.995;
      const cy = Math.cos(lat) * R * 0.995;
      const cz = Math.sin(lat) * Math.sin(lon) * R * 0.995;
      const n = new THREE.Vector3(cx, cy, cz).normalize();
      const up = new THREE.Vector3(0, 1, 0);
      const q = new THREE.Quaternion().setFromUnitVectors(up, n);
      const e = new THREE.Euler().setFromQuaternion(q);
      arr.push({ x: cx, y: cy, z: cz, rx: e.x, ry: e.y, rz: e.z, color: ap.color });
    });
    return arr;
  }, []);

  useFrame((state, delta) => {
    if (spinRef.current) spinRef.current.rotation.y += delta * 0.05;
    if (cloudRef.current) cloudRef.current.rotation.y += delta * 0.065;
    if (auraRef.current) {
      const t = state.clock.elapsedTime;
      auraRef.current.material.opacity = 0.16 + (active ? 0.14 : 0) + Math.sin(t * 1.5) * 0.04;
    }
  });

  return (
    <group position={POS}>
      <Atmosphere radius={R} color="#6BB6FF" active={active} auraRef={auraRef} />
      <group ref={spinRef} rotation={[0.41, 0, 0]}>
        <mesh material={surfaceMat}>
          <sphereGeometry args={[R, 72, 54]} />
        </mesh>
        {/* GREINCH section beacons on the surface */}
        {landmasses.map((l, i) => (
          <group key={`isl-${i}`} position={[l.x, l.y, l.z]} rotation={[l.rx, l.ry, l.rz]}>
            <mesh position={[0, 0.1, 0]}>
              <cylinderGeometry args={[0.35, 0.5, 0.5, 8]} />
              <meshStandardMaterial color="#F4EADE" roughness={0.6} />
            </mesh>
            <mesh position={[0, 0.7, 0]}>
              <cylinderGeometry args={[0.12, 0.2, 0.9, 6]} />
              <meshStandardMaterial color={l.color} emissive={l.color} emissiveIntensity={1.4} toneMapped={false} />
            </mesh>
          </group>
        ))}
      </group>
      <mesh ref={cloudRef} material={cloudMat} rotation={[0.41, 0, 0]}>
        <sphereGeometry args={[R * 1.025, 64, 48]} />
      </mesh>

      <Billboard position={[0, R + 10, 0]} follow>
        <Text fontSize={5} color="#6BB6FF" anchorX="center" anchorY="middle" outlineWidth={0.1} outlineColor="#050308" letterSpacing={0.05}>
          BACK TO EARTH
        </Text>
      </Billboard>
      <Billboard position={[0, R + 5, 0]} follow>
        <Text fontSize={1} color="#F4EADE" anchorX="center" anchorY="middle" letterSpacing={0.32}>
          {"> HOME · GREINCH ORIGIN"}
        </Text>
      </Billboard>

      {active && (
        <Html center position={[0, R + 1, 0]} zIndexRange={[10, 0]} distanceFactor={22}>
          <div className="pointer-events-none select-none whitespace-nowrap px-4 py-2 rounded-md bg-[rgba(11,20,40,0.85)] border border-[rgba(107,182,255,0.65)] text-[#F4EADE] font-mono text-xs uppercase tracking-[0.24em] shadow-[0_0_40px_rgba(107,182,255,0.5)]">
            <span className="text-[#6BB6FF]">[E]</span> Return to Earth
          </div>
        </Html>
      )}
      <pointLight color="#6BB6FF" intensity={1.8} distance={110} />
    </group>
  );
}
